/**
 * Custom Screen Builder — 3-Step Wizard Web Component
 *
 * Step 1: Screen Material (card selection)
 * Step 2: Screen Size (sliders + aspect ratio)
 * Step 3: Finishing (3 dropdowns: top/side/bottom)
 *
 * Pricing: client-side calculation via per-sq-inch formula.
 * Stores all selections in hidden line item properties for cart.
 */

class CustomScreenBuilder extends HTMLElement {
  constructor() {
    super();

    /* ---------- Draft Order API endpoint ---------- */
    this.DRAFT_ORDER_API = this.dataset.draftOrderApi || 'https://golfingsim-custom-screen.team-c70.workers.dev';

    /* ---------- config from data attrs ---------- */
    this.variantId = this.dataset.variantId;
    this.currency  = this.dataset.currency || 'USD';

    /* ---------- materials (populated from inline JSON script tag) ---------- */
    const jsonEl = this.querySelector('[data-csb-materials-json]');
    this.materials = jsonEl ? JSON.parse(jsonEl.textContent) : [];

    this.MIN_SIZE = 87;

    /* finishing border adds 2.5" per finished edge → 5" total per axis (display only) */
    this.FINISH_BORDER = 5;

    /* Finishing rates: dollars per linear inch of edge.
       Applied as: top/bottom = width × rate, side = 2 × height × rate.
       Total price = round-to-$5(base + all finishing raw). */
    this.FINISH_RATES = {
      'Unfinished Edge':                       0,
      'Black Vinyl (No Grommets)':             0.15,
      'Black Vinyl with Grommets':             0.16,
      'Black Vinyl with Grommets and 1" Loop': 0.19,
      'Black Vinyl with Grommets and Flaps':   0.34
    };

    /* ---------- state ---------- */
    this.currentStep = 1;
    this.selectedMaterial = null;
    this.height = 87;
    this.width  = 87;
    this.aspectRatio = 'aspect'; // 'aspect' | '4:3' | '16:9' | 'custom'
    this.sizeConfirmed = false;
    this.topFinish    = 'Black Vinyl with Grommets';
    this.sideFinish   = 'Black Vinyl with Grommets';
    this.bottomFinish = 'Black Vinyl with Grommets';
  }

  connectedCallback() {
    /* default to first material */
    if (this.materials.length > 0) {
      this.selectedMaterial = this.materials[0];
    }

    this.cacheElements();
    this.originalMaxH = parseInt(this.heightSlider.max, 10) || 115;
    this.originalMaxW = parseInt(this.widthSlider.max, 10) || 415;
    this.bindEvents();
    this.updatePrice();
    this.renderStep();

    /* No-JS safety: the Checkout button ships disabled so the $0/base variant can't be
       posted to /cart/add without JS. Now that the wizard is live and the submit is
       intercepted to create a correctly-priced draft order, enable it. */
    if (this.submitBtn) this.submitBtn.disabled = false;
  }

  /* ================================================================
     DOM references
     ================================================================ */
  cacheElements() {
    this.priceDisplay     = this.querySelector('[data-csb-price]');
    this.sidebarCartBtn   = this.querySelector('[data-csb-sidebar-cart]');
    this.stepEls          = this.querySelectorAll('[data-csb-step]');
    this.progressSteps    = this.querySelectorAll('[data-csb-progress-step]');
    this.progressSubsteps = this.querySelectorAll('[data-csb-progress-substep]');
    this.btnsBack          = this.querySelectorAll('[data-csb-back]');
    this.btnsNext          = this.querySelectorAll('[data-csb-next]');
    this.cartForm         = this.querySelector('[data-csb-cart-form]');
    this.submitBtn        = this.querySelector('[data-csb-submit]');

    /* step 2 elements */
    this.heightSlider  = this.querySelector('[data-csb-height]');
    this.widthSlider   = this.querySelector('[data-csb-width]');
    this.heightValue   = this.querySelector('[data-csb-height-val]');
    this.widthValue    = this.querySelector('[data-csb-width-val]');
    this.aspectSelect  = this.querySelector('[data-csb-aspect]');
    this.viewableSize  = this.querySelector('[data-csb-viewable]');
    this.totalSize     = this.querySelector('[data-csb-total]');
    this.confirmBtn    = this.querySelector('[data-csb-confirm]');

    /* step 2 room size inputs */
    this.roomHeightInput = this.querySelector('[data-csb-room-height]');
    this.roomWidthInput  = this.querySelector('[data-csb-room-width]');
    this.roomLimitNote   = this.querySelector('[data-csb-room-limit]');

    /* step 3 elements */
    this.topSelect    = this.querySelector('[data-csb-top-finish]');
    this.sideSelect   = this.querySelector('[data-csb-side-finish]');
    this.bottomSelect = this.querySelector('[data-csb-bottom-finish]');

    /* hidden inputs (line item properties) */
    this.inputMaterial      = this.querySelector('[data-prop-material]');
    this.inputSize          = this.querySelector('[data-prop-size]');
    this.inputTopFinish     = this.querySelector('[data-prop-top-finish]');
    this.inputSideFinish    = this.querySelector('[data-prop-side-finish]');
    this.inputBottomFinish  = this.querySelector('[data-prop-bottom-finish]');
    this.inputCalcPrice     = this.querySelector('[data-prop-calc-price]');
    this.inputCustomFlag    = this.querySelector('[data-prop-custom-flag]');

    /* modal */
    this.modalOverlay = this.querySelector('[data-csb-modal-overlay]');
    this.modalTitle   = this.querySelector('[data-csb-modal-title]');
    this.modalDesc    = this.querySelector('[data-csb-modal-desc]');
    this.modalImage   = this.querySelector('[data-csb-modal-img]');
    this.modalFeatures= this.querySelector('[data-csb-modal-features]');
    this.modalDots    = this.querySelector('[data-csb-modal-dots]');
    this.modalImages  = [];
    this.modalImageIdx = 0;
  }

  /* ================================================================
     Events
     ================================================================ */
  bindEvents() {
    /* Material card clicks and keyboard activation */
    this.querySelectorAll('[data-csb-material]').forEach(card => {
      card.addEventListener('click', (e) => {
        /* don't trigger when clicking "More Info" */
        if (e.target.closest('[data-csb-more-info]')) return;
        const idx = parseInt(card.dataset.csbMaterial, 10);
        this.selectMaterial(idx);
      });
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (e.target.closest('[data-csb-more-info]')) return;
          const idx = parseInt(card.dataset.csbMaterial, 10);
          this.selectMaterial(idx);
        }
      });
    });

    /* More Info buttons */
    this.querySelectorAll('[data-csb-more-info]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const idx = parseInt(btn.dataset.csbMoreInfo, 10);
        this.openModal(idx);
      });
    });

    /* Modal close */
    if (this.modalOverlay) {
      this.modalOverlay.addEventListener('click', (e) => {
        if (e.target === this.modalOverlay || e.target.closest('[data-csb-modal-close]')) {
          this.closeModal();
        }
      });
    }

    /* Modal carousel arrows */
    const prevArrow = this.querySelector('[data-csb-modal-prev]');
    const nextArrow = this.querySelector('[data-csb-modal-next]');
    if (prevArrow) prevArrow.addEventListener('click', () => this.modalNavigate(-1));
    if (nextArrow) nextArrow.addEventListener('click', () => this.modalNavigate(1));

    /* Navigation buttons */
    this.btnsBack.forEach(btn => btn.addEventListener('click', () => this.goToStep(this.currentStep - 1)));
    this.btnsNext.forEach(btn => btn.addEventListener('click', () => this.goToStep(this.currentStep + 1)));

    /* Progress step labels — click to jump back to a completed step */
    this.progressSteps.forEach(el => {
      el.addEventListener('click', () => {
        const s = parseInt(el.dataset.csbProgressStep, 10);
        if (s < this.currentStep) this.goToStep(s);
      });
    });

    /* Step 2: sliders */
    if (this.heightSlider) {
      this.heightSlider.addEventListener('input', () => this.handleSlider());
    }
    if (this.widthSlider) {
      this.widthSlider.addEventListener('input', () => this.handleSlider());
    }
    if (this.aspectSelect) {
      this.aspectSelect.addEventListener('change', () => this.handleAspectChange());
    }
    if (this.confirmBtn) {
      this.confirmBtn.addEventListener('click', () => this.confirmSize());
    }

    /* Step 2: room size inputs */
    if (this.roomHeightInput) {
      this.roomHeightInput.addEventListener('input', () => this.handleRoomSize());
    }
    if (this.roomWidthInput) {
      this.roomWidthInput.addEventListener('input', () => this.handleRoomSize());
    }

    /* Step 3: finishing dropdowns */
    if (this.topSelect) this.topSelect.addEventListener('change', () => { this.topFinish = this.topSelect.value; this.updatePrice(); });
    if (this.sideSelect) this.sideSelect.addEventListener('change', () => { this.sideFinish = this.sideSelect.value; this.updatePrice(); });
    if (this.bottomSelect) this.bottomSelect.addEventListener('change', () => { this.bottomFinish = this.bottomSelect.value; this.updatePrice(); });

    /* Sidebar Checkout button */
    if (this.sidebarCartBtn) {
      this.sidebarCartBtn.addEventListener('click', () => {
        if (this.currentStep < 3) {
          this.goToStep(3);
        } else {
          this.submitDraftOrder();
        }
      });
    }

    /* Cart form submission — intercept and create Draft Order instead */
    if (this.cartForm) {
      this.cartForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.submitDraftOrder();
      });
    }
  }

  /* ================================================================
     Material selection
     ================================================================ */
  selectMaterial(idx) {
    this.selectedMaterial = this.materials[idx];

    /* Update card visuals and ARIA state */
    this.querySelectorAll('[data-csb-material]').forEach(card => {
      const i = parseInt(card.dataset.csbMaterial, 10);
      const selected = i === idx;
      card.classList.toggle('csb-material-card--selected', selected);
      card.setAttribute('aria-checked', selected ? 'true' : 'false');
    });

    /* Update width slider max per material */
    if (this.widthSlider && this.selectedMaterial.maxWidth) {
      this.widthSlider.max = this.selectedMaterial.maxWidth;
      if (this.width > this.selectedMaterial.maxWidth) {
        this.width = this.selectedMaterial.maxWidth;
        this.widthSlider.value = this.width;
      }
      /* Update displayed max label */
      const group = this.widthSlider.closest('.csb-slider-group');
      if (group) {
        const span = group.querySelector('.csb-slider__max');
        if (span) span.textContent = this.selectedMaterial.maxWidth + '"';
      }
    }

    this.updateSizeDisplay();
    this.updatePrice();
  }

  /* ================================================================
     Step 2: Size handling
     ================================================================ */
  handleSlider() {
    let h = parseInt(this.heightSlider.value, 10);
    let w = parseInt(this.widthSlider.value, 10);

    const ratio = this.aspectSelect ? this.aspectSelect.value : 'custom';

    if (ratio === '4:3') {
      /* Keep width locked to height × 4/3 */
      w = Math.round(h * (4 / 3));
      w = Math.min(w, parseInt(this.widthSlider.max, 10));
      w = Math.max(w, parseInt(this.widthSlider.min, 10));
      this.widthSlider.value = w;
    } else if (ratio === '16:9') {
      w = Math.round(h * (16 / 9));
      w = Math.min(w, parseInt(this.widthSlider.max, 10));
      w = Math.max(w, parseInt(this.widthSlider.min, 10));
      this.widthSlider.value = w;
    }

    this.height = h;
    this.width  = w;
    this.sizeConfirmed = false;

    this.updateSizeDisplay();
    this.updatePrice();
  }

  handleAspectChange() {
    this.aspectRatio = this.aspectSelect.value;

    if (this.aspectRatio === '4:3' || this.aspectRatio === '16:9') {
      /* recalculate width from current height */
      const factor = this.aspectRatio === '4:3' ? 4/3 : 16/9;
      let w = Math.round(this.height * factor);
      const wMax = parseInt(this.widthSlider.max, 10);
      const wMin = parseInt(this.widthSlider.min, 10);
      w = Math.min(w, wMax);
      w = Math.max(w, wMin);
      this.width = w;
      this.widthSlider.value = w;
      this.widthSlider.disabled = true;
    } else {
      this.widthSlider.disabled = false;
    }

    this.sizeConfirmed = false;
    this.updateSizeDisplay();
    this.updatePrice();
  }

  handleRoomSize() {
    const CLEARANCE = 12; /* inches to leave for mounting on each axis */
    const roomH = parseInt(this.roomHeightInput.value, 10);
    const roomW = parseInt(this.roomWidthInput.value,  10);

    const materialMaxW = (this.selectedMaterial && this.selectedMaterial.maxWidth)
      ? this.selectedMaterial.maxWidth
      : this.originalMaxW;
    const absoluteMaxH = this.originalMaxH;
    const absoluteMaxW = materialMaxW;

    let capH = absoluteMaxH;
    let capW = absoluteMaxW;
    const parts = [];

    if (!isNaN(roomH) && roomH > 0) {
      capH = Math.max(this.MIN_SIZE, Math.min(absoluteMaxH, roomH - CLEARANCE));
      parts.push('max height ' + capH + '"');
    }
    if (!isNaN(roomW) && roomW > 0) {
      capW = Math.max(this.MIN_SIZE, Math.min(absoluteMaxW, roomW - CLEARANCE));
      parts.push('max width ' + capW + '"');
    }

    /* Update slider maxes */
    this.heightSlider.max = capH;
    this.widthSlider.max  = capW;

    /* Update displayed max labels */
    const hGroup = this.heightSlider.closest('.csb-slider-group');
    if (hGroup) { const s = hGroup.querySelector('.csb-slider__max'); if (s) s.textContent = capH + '"'; }
    const wGroup = this.widthSlider.closest('.csb-slider-group');
    if (wGroup) { const s = wGroup.querySelector('.csb-slider__max'); if (s) s.textContent = capW + '"'; }

    /* Clamp current values if they exceed the new cap */
    if (this.height > capH) {
      this.height = capH;
      this.heightSlider.value = capH;
    }
    if (this.width > capW) {
      this.width = capW;
      this.widthSlider.value = capW;
    }

    /* Show/hide limit note */
    if (this.roomLimitNote) {
      if (parts.length > 0) {
        this.roomLimitNote.textContent = 'Based on your room size, your screen is limited to ' + parts.join(', ') + ' (allowing 12\u2033 mounting clearance).';
        this.roomLimitNote.style.display = '';
      } else {
        this.roomLimitNote.style.display = 'none';
      }
    }

    this.sizeConfirmed = false;
    this.updateSizeDisplay();
    this.updatePrice();
  }

  confirmSize() {
    this.sizeConfirmed = true;
    if (this.confirmBtn) {
      this.confirmBtn.textContent = 'Confirmed ✓';
      this.confirmBtn.classList.add('csb-size-preview__confirm--confirmed');
    }
    this.goToStep(this.currentStep + 1);
  }

  updateSizeDisplay() {
    if (this.heightValue) this.heightValue.textContent = this.height + '"';
    if (this.widthValue)  this.widthValue.textContent  = this.width + '"';

    /* Viewable = slider values */
    const viewH = this.height;
    const viewW = this.width;
    if (this.viewableSize) this.viewableSize.textContent = viewH + '" × ' + viewW + '"';

    /* Total = viewable + finishing border */
    const totalH = viewH + this.FINISH_BORDER;
    const totalW = viewW + this.FINISH_BORDER;
    if (this.totalSize) this.totalSize.textContent = totalH + '" × ' + totalW + '"';

    /* Reset confirm state */
    if (this.confirmBtn && !this.sizeConfirmed) {
      this.confirmBtn.textContent = 'Confirm';
      this.confirmBtn.classList.remove('csb-size-preview__confirm--confirmed');
    }

    this.updateScreenViz();
  }

  updateScreenViz() {
    const svg = this.querySelector('[data-csb-screen-viz]');
    if (!svg) return;

    const screen  = svg.querySelector('[data-csb-viz-screen]');
    const person  = svg.querySelector('[data-csb-viz-person]');
    const wLabel  = svg.querySelector('[data-csb-viz-w-label]');
    const hLabel  = svg.querySelector('[data-csb-viz-h-label]');

    const totalH = this.height + this.FINISH_BORDER;
    const totalW = this.width  + this.FINISH_BORDER;
    const personH = 69; /* 5'9" in inches */

    const PAD = 14;
    const LABEL = 12;
    const sceneW = totalW + personH * 0.35 + PAD + LABEL;
    const sceneH = Math.max(totalH, personH) + PAD + LABEL;

    svg.setAttribute('viewBox', `0 0 ${sceneW} ${sceneH}`);

    const screenX = LABEL;
    const screenY = LABEL;
    screen.setAttribute('x', screenX);
    screen.setAttribute('y', screenY);
    screen.setAttribute('width', totalW);
    screen.setAttribute('height', totalH);

    /* Width label centred above screen */
    wLabel.setAttribute('x', screenX + totalW / 2);
    wLabel.setAttribute('y', LABEL - 3);
    wLabel.textContent = totalW + '"';

    /* Height label centred left of screen */
    const hMid = screenY + totalH / 2;
    hLabel.setAttribute('transform', `rotate(-90, ${LABEL - 4}, ${hMid})`);
    hLabel.setAttribute('x', LABEL - 4);
    hLabel.setAttribute('y', hMid + 3);
    hLabel.textContent = totalH + '"';

    /* Person – bottom-aligned with screen, right of screen */
    const personScale = personH / 69;
    const px = screenX + totalW + PAD + (personH * 0.15);
    const personBodyBottom = 62; /* feet are at local y=62 in the SVG figure */
    const py = screenY + totalH - personBodyBottom * personScale;
    person.setAttribute('transform', `translate(${px}, ${py}) scale(${personScale})`);
  }

  /* ================================================================
     Pricing
     ================================================================ */
  updatePrice() {
    if (!this.selectedMaterial) return;

    const m = this.selectedMaterial;

    /* Base material cost: width-only, in dollars */
    const baseDollars = this.width * m.ratePerInch;

    /* Finishing cost: per-edge raw dollars, NO per-edge rounding.
       top/bottom edge = width × rate, side = 2 × height × rate. */
    const topRate    = this.FINISH_RATES[this.topFinish]    || 0;
    const sideRate   = this.FINISH_RATES[this.sideFinish]   || 0;
    const bottomRate = this.FINISH_RATES[this.bottomFinish] || 0;

    const topDollars    = this.width  * topRate;
    const sideDollars   = 2 * this.height * sideRate;
    const bottomDollars = this.width  * bottomRate;

    /* Single round to nearest $5 on the whole total */
    const totalDollars = baseDollars + topDollars + sideDollars + bottomDollars;
    const finalCents = Math.round(totalDollars / 5) * 500;

    const formatted = this.formatMoney(finalCents);

    if (this.priceDisplay) this.priceDisplay.textContent = formatted;

    /* Store for cart submission */
    this._calculatedPrice = formatted;
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: this.currency
    }).format(cents / 100);
  }

  /* ================================================================
     Step navigation
     ================================================================ */
  goToStep(step) {
    if (step < 1 || step > 3) return;
    this.currentStep = step;
    this.renderStep();
  }

  renderStep() {
    /* Show active step */
    this.stepEls.forEach(el => {
      const s = parseInt(el.dataset.csbStep, 10);
      el.classList.toggle('csb-step--active', s === this.currentStep);
    });

    /* Update nav buttons */
    this.btnsBack.forEach(btn => {
      btn.disabled = this.currentStep === 1;
      btn.style.display = this.currentStep === 1 ? 'none' : '';
    });
    this.btnsNext.forEach(btn => {
      btn.style.display = this.currentStep === 3 ? 'none' : '';
    });

    /* Update progress sidebar */
    this.progressSteps.forEach(el => {
      const s = parseInt(el.dataset.csbProgressStep, 10);
      el.classList.remove('csb-progress__step--active', 'csb-progress__step--completed');
      if (s < this.currentStep) {
        el.classList.add('csb-progress__step--completed');
      } else if (s === this.currentStep) {
        el.classList.add('csb-progress__step--active');
      }
    });

    /* Substeps disabled — keep sidebar stationary */

    /* Sidebar cart button */
    if (this.sidebarCartBtn) {
      if (this.currentStep === 3) {
        this.sidebarCartBtn.classList.add('csb-sidebar__cart-btn--active');
        this.sidebarCartBtn.classList.add('csb-sidebar__cart-btn--show-mobile');
      } else {
        this.sidebarCartBtn.classList.remove('csb-sidebar__cart-btn--active');
        this.sidebarCartBtn.classList.remove('csb-sidebar__cart-btn--show-mobile');
      }
    }

    /* Update size display when entering Step 2 */
    if (this.currentStep === 2) {
      this.updateSizeDisplay();
    }

    this.updatePrice();
  }

  /* ================================================================
     Modal (More Info)
     ================================================================ */
  openModal(idx) {
    const mat = this.materials[idx];
    if (!mat) return;

    if (this.modalTitle) this.modalTitle.textContent = mat.name;
    if (this.modalDesc)  this.modalDesc.textContent  = mat.longDesc || mat.desc;

    /* Features list */
    if (this.modalFeatures) {
      this.modalFeatures.innerHTML = '';
      (mat.features || []).forEach(f => {
        const li = document.createElement('li');
        li.textContent = f;
        this.modalFeatures.appendChild(li);
      });
    }

    /* Images — preload all so transitions are instant */
    this.modalImages = mat.images || [mat.image];
    this.modalImages.forEach(src => { const img = new Image(); img.src = src; });
    this.modalImageIdx = 0;
    this.renderModalImage();

    if (this.modalOverlay) this.modalOverlay.classList.add('csb-modal-overlay--active');

    /* Prevent body scroll */
    document.body.style.overflow = 'hidden';
  }

  closeModal() {
    if (this.modalOverlay) this.modalOverlay.classList.remove('csb-modal-overlay--active');
    document.body.style.overflow = '';
  }

  modalNavigate(dir) {
    this.modalImageIdx += dir;
    if (this.modalImageIdx < 0) this.modalImageIdx = this.modalImages.length - 1;
    if (this.modalImageIdx >= this.modalImages.length) this.modalImageIdx = 0;
    this.renderModalImage();
  }

  renderModalImage() {
    if (this.modalImage && this.modalImages.length > 0) {
      this.modalImage.classList.add('csb-modal__image--fade');
      setTimeout(() => {
        this.modalImage.src = this.modalImages[this.modalImageIdx];
        this.modalImage.alt = (this.materials[0] || {}).name || 'Screen material';
        this.modalImage.classList.remove('csb-modal__image--fade');
      }, 150);
    }
    if (this.modalDots) {
      this.modalDots.innerHTML = '';
      this.modalImages.forEach((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'csb-modal__carousel-dot' + (i === this.modalImageIdx ? ' csb-modal__carousel-dot--active' : '');
        dot.addEventListener('click', () => {
          this.modalImageIdx = i;
          this.renderModalImage();
        });
        this.modalDots.appendChild(dot);
      });
    }
  }

  /* ================================================================
     Cart submission — Draft Order flow
     ================================================================ */
  async submitDraftOrder() {
    if (!this.selectedMaterial) return;

    /* Disable button and show loading state */
    const submitBtn = this.cartForm.querySelector('[type="submit"]');
    const originalText = submitBtn ? submitBtn.textContent : '';
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Creating your order...';
    }

    try {
      const res = await fetch(this.DRAFT_ORDER_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          material:     this.selectedMaterial.name,
          height:       this.height,
          width:        this.width,
          topFinish:    this.topFinish,
          sideFinish:   this.sideFinish,
          bottomFinish: this.bottomFinish,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to create order');
      }

      /* Redirect to Shopify checkout via the draft order invoice URL */
      window.location.href = data.invoice_url;

    } catch (err) {
      console.error('Draft order failed:', err);
      alert('Something went wrong creating your order. Please try again or contact us.');
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = originalText;
      }
    }
  }
}

/* Register */
if (!customElements.get('custom-screen-builder')) {
  customElements.define('custom-screen-builder', CustomScreenBuilder);
}


